import { Pick, type Plan, type PlaceTo, type ReadPlace } from '@trippath/shared';
import { useTranslation } from 'react-i18next';

import { stopDays } from '../../pages/city/cityDays';

/** A place read, whether it is taken in, and where to. */
export interface Row {
  place: ReadPlace;
  take: boolean;
  to: PlaceTo;
}

/** Where a place goes unless chosen otherwise: its stop within 30 km, without a day; else a new
 * stop in its town after the nearest; else none. */
export function rowOf(place: ReadPlace): Row {
  const { stop, nearest, town } = place.near;
  const to: PlaceTo =
    stop !== null
      ? { stop, day: null }
      : nearest && town
        ? { after: nearest.stop, city: town.city, country: town.country }
        : null;
  return { place, take: true, to };
}

/** A day as the rows say it: `Wed 10 March`, `K 10. märts`. */
export function daySaid(day: string, language: string): string {
  return new Intl.DateTimeFormat(language === 'et' ? 'et-EE' : 'en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${day}T00:00:00Z`));
}

/** The value of a row's stop choice. */
function chosen(to: PlaceTo): string {
  if (to === null) return 'none';
  return 'stop' in to ? `stop:${to.stop}` : 'new';
}

/** The places read, each with whether it is taken in, its stop and its day. */
export function Rows({
  plan,
  rows,
  head,
  onChange,
}: {
  plan: Plan;
  rows: Row[];
  /** The first column's heading: a list's place, a file's point. */
  head: string;
  onChange: (rows: Row[]) => void;
}) {
  const { t, i18n } = useTranslation();
  const stopOf = (id: string) => plan.stops.find((stop) => stop.id === id);
  const set = (at: number, row: Row) => {
    onChange(rows.map((was, index) => (index === at ? row : was)));
  };
  const every = rows.length > 0 && rows.every((row) => row.take);
  return (
    <div className="table-scroll">
      <table className="import-table">
        <thead>
          <tr>
            <th scope="col">
              <input
                type="checkbox"
                aria-label={t('importPlaces.all')}
                checked={every}
                onChange={() => {
                  onChange(rows.map((row) => ({ ...row, take: !every })));
                }}
              />
            </th>
            <th scope="col">{head}</th>
            <th scope="col">{t('importPlaces.kind')}</th>
            <th scope="col">{t('importPlaces.stop')}</th>
            <th scope="col">{t('importPlaces.day')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, at) => {
            const { place, to } = row;
            const nearest = place.near.nearest && stopOf(place.near.nearest.stop);
            const town = place.near.town;
            // The stop chosen, when one of the trip's.
            const going = to !== null && 'stop' in to ? to : null;
            const stop = going && stopOf(going.stop);
            return (
              <tr key={`${place.name} ${String(place.lat)} ${String(place.lon)}`}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={t('importPlaces.take')}
                    checked={row.take}
                    onChange={() => {
                      set(at, { ...row, take: !row.take });
                    }}
                  />
                </td>
                <td>
                  <span className="import-name">{place.name}</span>
                  <span className="import-soft">
                    {place.address ??
                      (nearest && place.near.nearest && town && place.near.stop === null
                        ? t('importPlaces.away', {
                            city: town.city,
                            km: place.near.nearest.km,
                            from: nearest.city,
                          })
                        : '')}
                  </span>
                </td>
                <td>{place.kind ?? '—'}</td>
                <td>
                  <Pick
                    label={t('importPlaces.stop')}
                    quiet
                    searchable={false}
                    value={chosen(to)}
                    options={[
                      ...plan.stops.map((one) => ({
                        value: `stop:${one.id}`,
                        label: one.city,
                      })),
                      ...(nearest && town
                        ? [
                            {
                              value: 'new',
                              label: t('importPlaces.newStopAfter', {
                                city: town.city,
                                after: nearest.city,
                              }),
                            },
                          ]
                        : []),
                      { value: 'none', label: t('importPlaces.none') },
                    ]}
                    onChange={(value) => {
                      let next: PlaceTo = null;
                      if (value.startsWith('stop:')) next = { stop: value.slice(5), day: null };
                      if (value === 'new' && nearest && town) {
                        next = { after: nearest.id, city: town.city, country: town.country };
                      }
                      set(at, { ...row, to: next });
                    }}
                  />
                </td>
                <td>
                  {going && stop ? (
                    <Pick
                      label={t('importPlaces.day')}
                      quiet
                      searchable={false}
                      value={going.day ?? ''}
                      options={[
                        { value: '', label: t('importPlaces.noDay') },
                        ...stopDays(stop).map((day) => ({
                          value: day,
                          label: daySaid(day, i18n.language),
                        })),
                      ]}
                      onChange={(day) => {
                        set(at, {
                          ...row,
                          to: { stop: stop.id, day: day === '' ? null : day },
                        });
                      }}
                    />
                  ) : (
                    t('importPlaces.noneDay')
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
